import type {
  FieldMappingEntry,
  ImportDiscovery,
  ImportMapping,
  IssueTypeMappingEntry,
  StatusMappingEntry,
  UserMappingEntry,
} from '@servicetim/shared';
import { FIELD_TYPES, type FieldType, STATUS_CATEGORIES } from '@servicetim/shared';

/** `ImportDiscovery`'s per-list item shapes aren't exported on their own — index into the array type. */
type DiscoveredField = ImportDiscovery['fields'][number];
type DiscoveredStatus = ImportDiscovery['statuses'][number];
type DiscoveredPriority = ImportDiscovery['priorities'][number];
type DiscoveredUser = ImportDiscovery['users'][number];
type DiscoveredIssueType = ImportDiscovery['issueTypes'][number];

import { useFieldOptions } from '@servicetim/ui/api';
import {
  Badge,
  EmptyState,
  Input,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@servicetim/ui/components';
import { type SelectOption, SimpleSelect } from '../../projects/ui/simple-select';
import type { MappingTargets } from './suggest';

export interface MappingTabsProps {
  discovery: ImportDiscovery;
  mapping: ImportMapping;
  onChange: (next: ImportMapping) => void;
  targets: MappingTargets;
}

const titleCase = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1).replace(/_/g, ' ');

/**
 * The six mapping tabs (S-09-03): source id → target picker, "create as <type>" / "create in
 * draft" sub-forms shown inline once "Create new" is chosen. Purely a controlled component
 * (`mapping` + `onChange`) — no data fetching except the Options tab's per-field option list,
 * so most of it renders and is tested with plain props (`mapping-tabs.test.tsx`).
 */
export function MappingTabs({ discovery, mapping, onChange, targets }: MappingTabsProps) {
  return (
    <Tabs defaultValue="fields">
      <TabsList aria-label="Mapping">
        <TabsTrigger value="fields">Fields</TabsTrigger>
        <TabsTrigger value="statuses">Statuses</TabsTrigger>
        <TabsTrigger value="priorities">Priorities</TabsTrigger>
        <TabsTrigger value="users">Users</TabsTrigger>
        <TabsTrigger value="issueTypes">Issue types</TabsTrigger>
        <TabsTrigger value="options">Options</TabsTrigger>
      </TabsList>

      <TabsContent value="fields">
        <FieldsTab
          fields={discovery.fields}
          mapping={mapping}
          targetFields={targets.fields}
          onChange={onChange}
        />
      </TabsContent>
      <TabsContent value="statuses">
        <StatusesTab
          statuses={discovery.statuses}
          mapping={mapping}
          targetStatuses={targets.statuses}
          onChange={onChange}
        />
      </TabsContent>
      <TabsContent value="priorities">
        <PrioritiesTab
          priorities={discovery.priorities}
          mapping={mapping}
          targetPriorities={targets.priorities}
          onChange={onChange}
        />
      </TabsContent>
      <TabsContent value="users">
        <UsersTab
          users={discovery.users}
          mapping={mapping}
          targetUsers={targets.users}
          onChange={onChange}
        />
      </TabsContent>
      <TabsContent value="issueTypes">
        <IssueTypesTab
          issueTypes={discovery.issueTypes}
          mapping={mapping}
          targetIssueTypes={targets.issueTypes}
          onChange={onChange}
        />
      </TabsContent>
      <TabsContent value="options">
        <OptionsTab
          fields={discovery.fields}
          mapping={mapping}
          targets={targets}
          onChange={onChange}
        />
      </TabsContent>
    </Tabs>
  );
}

/* ----------------------------- fields ----------------------------- */

function FieldsTab({
  fields,
  mapping,
  targetFields,
  onChange,
}: {
  fields: readonly DiscoveredField[];
  mapping: ImportMapping;
  targetFields: MappingTargets['fields'];
  onChange: (next: ImportMapping) => void;
}) {
  if (fields.length === 0) {
    return <EmptyState title="No custom fields discovered" size="sm" />;
  }
  const targetOptions: SelectOption[] = targetFields.map((f) => ({ value: f.key, label: f.label }));
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Source field</TableHead>
          <TableHead>Target</TableHead>
          <TableHead>New field</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {fields.map((f) => {
          const entry = mapping.fields[f.id];
          const value =
            entry === 'skip' ? 'skip' : entry && 'fieldKey' in entry ? entry.fieldKey : 'create';
          const create =
            entry && typeof entry === 'object' && 'create' in entry ? entry.create : null;
          const setEntry = (next: FieldMappingEntry) =>
            onChange({ ...mapping, fields: { ...mapping.fields, [f.id]: next } });
          return (
            <TableRow key={f.id}>
              <TableCell>
                <div className="font-medium">{f.label}</div>
                {f.type ? (
                  <Badge variant="outline">{f.type}</Badge>
                ) : (
                  <span className="text-fg-muted text-xs">unrecognized type</span>
                )}
              </TableCell>
              <TableCell>
                <SimpleSelect
                  aria-label={`Target for ${f.label}`}
                  value={value}
                  onChange={(v) => {
                    if (v === 'skip') setEntry('skip');
                    else if (v === 'create') {
                      setEntry({
                        create: {
                          type: create?.type ?? f.type ?? 'text',
                          label: f.label,
                          config: {},
                        },
                      });
                    } else if (v) {
                      setEntry({ fieldKey: v });
                    }
                  }}
                  options={[
                    { value: 'create', label: 'Create new field' },
                    { value: 'skip', label: 'Skip' },
                    ...targetOptions,
                  ]}
                />
              </TableCell>
              <TableCell>
                {value === 'create' ? (
                  <div className="flex gap-2">
                    <Input
                      aria-label={`New field label for ${f.label}`}
                      value={create?.label ?? f.label}
                      onChange={(e) =>
                        setEntry({
                          create: {
                            type: create?.type ?? f.type ?? 'text',
                            label: e.target.value,
                            config: {},
                          },
                        })
                      }
                      className="w-40"
                    />
                    <SimpleSelect
                      aria-label={`New field type for ${f.label}`}
                      value={create?.type ?? f.type ?? 'text'}
                      onChange={(t) =>
                        t &&
                        setEntry({
                          create: {
                            type: t as FieldType,
                            label: create?.label ?? f.label,
                            config: {},
                          },
                        })
                      }
                      options={FIELD_TYPES.filter((t) => t !== 'ticket_lookup').map((t) => ({
                        value: t,
                        label: titleCase(t),
                      }))}
                    />
                  </div>
                ) : null}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

/* ----------------------------- statuses ----------------------------- */

function StatusesTab({
  statuses,
  mapping,
  targetStatuses,
  onChange,
}: {
  statuses: readonly DiscoveredStatus[];
  mapping: ImportMapping;
  targetStatuses: MappingTargets['statuses'];
  onChange: (next: ImportMapping) => void;
}) {
  if (statuses.length === 0) {
    return <EmptyState title="No statuses discovered" size="sm" />;
  }
  const targetOptions: SelectOption[] = targetStatuses.map((s) => ({ value: s.id, label: s.name }));
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Source status</TableHead>
          <TableHead>Target</TableHead>
          <TableHead>New status</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {statuses.map((s) => {
          const entry = mapping.statuses[s.id];
          const value = entry && 'statusId' in entry ? entry.statusId : 'create';
          const create = entry && 'createInDraft' in entry ? entry.createInDraft : null;
          const setEntry = (next: StatusMappingEntry) =>
            onChange({ ...mapping, statuses: { ...mapping.statuses, [s.id]: next } });
          return (
            <TableRow key={s.id}>
              <TableCell>
                <div className="font-medium">{s.name}</div>
                {s.category ? (
                  <Badge variant="outline">{s.category}</Badge>
                ) : (
                  <span className="text-fg-muted text-xs">unknown category</span>
                )}
              </TableCell>
              <TableCell>
                <SimpleSelect
                  aria-label={`Target for ${s.name}`}
                  value={value}
                  onChange={(v) => {
                    if (v === 'create') {
                      setEntry({
                        createInDraft: {
                          name: s.name,
                          category: create?.category ?? s.category ?? 'todo',
                        },
                      });
                    } else if (v) {
                      setEntry({ statusId: v });
                    }
                  }}
                  options={[{ value: 'create', label: 'Create in draft' }, ...targetOptions]}
                />
              </TableCell>
              <TableCell>
                {value === 'create' ? (
                  <div className="flex gap-2">
                    <Input
                      aria-label={`New status name for ${s.name}`}
                      value={create?.name ?? s.name}
                      onChange={(e) =>
                        setEntry({
                          createInDraft: {
                            name: e.target.value,
                            category: create?.category ?? s.category ?? 'todo',
                          },
                        })
                      }
                      className="w-40"
                    />
                    <SimpleSelect
                      aria-label={`New status category for ${s.name}`}
                      value={create?.category ?? s.category ?? 'todo'}
                      onChange={(cat) =>
                        cat &&
                        setEntry({
                          createInDraft: {
                            name: create?.name ?? s.name,
                            category: cat as (typeof STATUS_CATEGORIES)[number],
                          },
                        })
                      }
                      options={STATUS_CATEGORIES.map((c) => ({ value: c, label: titleCase(c) }))}
                    />
                  </div>
                ) : null}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

/* ----------------------------- priorities ----------------------------- */

function PrioritiesTab({
  priorities,
  mapping,
  targetPriorities,
  onChange,
}: {
  priorities: readonly DiscoveredPriority[];
  mapping: ImportMapping;
  targetPriorities: MappingTargets['priorities'];
  onChange: (next: ImportMapping) => void;
}) {
  if (priorities.length === 0) {
    return <EmptyState title="No priorities discovered" size="sm" />;
  }
  const targetOptions: SelectOption[] = targetPriorities.map((p) => ({
    value: p.id,
    label: p.name,
  }));
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Source priority</TableHead>
          <TableHead>Target</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {priorities.map((p) => {
          const entry = mapping.priorities[p.id];
          const value = entry ? entry.priorityId : 'none';
          return (
            <TableRow key={p.id}>
              <TableCell className="font-medium">{p.name}</TableCell>
              <TableCell>
                <SimpleSelect
                  aria-label={`Target for ${p.name}`}
                  value={value}
                  onChange={(v) =>
                    onChange({
                      ...mapping,
                      priorities: {
                        ...mapping.priorities,
                        [p.id]: v && v !== 'none' ? { priorityId: v } : null,
                      },
                    })
                  }
                  options={[{ value: 'none', label: '(none)' }, ...targetOptions]}
                />
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

/* ----------------------------- users ----------------------------- */

function UsersTab({
  users,
  mapping,
  targetUsers,
  onChange,
}: {
  users: readonly DiscoveredUser[];
  mapping: ImportMapping;
  targetUsers: MappingTargets['users'];
  onChange: (next: ImportMapping) => void;
}) {
  const rows = users.filter((u): u is DiscoveredUser & { email: string } => u.email !== null);
  if (rows.length === 0) {
    return <EmptyState title="No emailed users discovered" size="sm" />;
  }
  const targetOptions: SelectOption[] = targetUsers.map((u) => ({
    value: u.id,
    label: `${u.displayName} <${u.email}>`,
  }));
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Source user</TableHead>
          <TableHead>Target</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((u) => {
          const entry: UserMappingEntry | undefined = mapping.users[u.email];
          const value = entry && entry !== 'create_imported' ? entry.userId : 'create_imported';
          return (
            <TableRow key={u.email}>
              <TableCell>
                <div className="font-medium">{u.displayName}</div>
                <div className="text-fg-muted text-xs">{u.email}</div>
              </TableCell>
              <TableCell>
                <SimpleSelect
                  aria-label={`Target for ${u.email}`}
                  value={value}
                  onChange={(v) =>
                    onChange({
                      ...mapping,
                      users: {
                        ...mapping.users,
                        [u.email]: v && v !== 'create_imported' ? { userId: v } : 'create_imported',
                      },
                    })
                  }
                  options={[
                    { value: 'create_imported', label: 'Create imported user' },
                    ...targetOptions,
                  ]}
                />
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

/* ----------------------------- issue types ----------------------------- */

function IssueTypesTab({
  issueTypes,
  mapping,
  targetIssueTypes,
  onChange,
}: {
  issueTypes: readonly DiscoveredIssueType[];
  mapping: ImportMapping;
  targetIssueTypes: MappingTargets['issueTypes'];
  onChange: (next: ImportMapping) => void;
}) {
  if (issueTypes.length === 0) {
    return <EmptyState title="No issue types discovered" size="sm" />;
  }
  const targetOptions: SelectOption[] = targetIssueTypes.map((t) => ({
    value: t.id,
    label: t.name,
  }));
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Source issue type</TableHead>
          <TableHead>Target</TableHead>
          <TableHead>New issue type</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {issueTypes.map((t) => {
          const entry = mapping.issueTypes[t.id];
          const value = entry && 'issueTypeId' in entry ? entry.issueTypeId : 'create';
          const create = entry && 'create' in entry ? entry.create : null;
          const setEntry = (next: IssueTypeMappingEntry) =>
            onChange({ ...mapping, issueTypes: { ...mapping.issueTypes, [t.id]: next } });
          return (
            <TableRow key={t.id}>
              <TableCell className="font-medium">{t.name}</TableCell>
              <TableCell>
                <SimpleSelect
                  aria-label={`Target for ${t.name}`}
                  value={value}
                  onChange={(v) => {
                    if (v === 'create') setEntry({ create: { name: create?.name ?? t.name } });
                    else if (v) setEntry({ issueTypeId: v });
                  }}
                  options={[{ value: 'create', label: 'Create new issue type' }, ...targetOptions]}
                />
              </TableCell>
              <TableCell>
                {value === 'create' ? (
                  <Input
                    aria-label={`New issue type name for ${t.name}`}
                    value={create?.name ?? t.name}
                    onChange={(e) => setEntry({ create: { name: e.target.value } })}
                    className="w-40"
                  />
                ) : null}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

/* ----------------------------- options ----------------------------- */

function OptionsTab({
  fields,
  mapping,
  targets,
  onChange,
}: {
  fields: readonly DiscoveredField[];
  mapping: ImportMapping;
  targets: MappingTargets;
  onChange: (next: ImportMapping) => void;
}) {
  const selectFields = fields.filter(
    (f) => f.type === 'single_select' || f.type === 'multi_select',
  );
  if (selectFields.length === 0) {
    return <EmptyState title="No select fields discovered" size="sm" />;
  }
  return (
    <div className="space-y-6">
      {selectFields.map((f) => {
        const entry = mapping.fields[f.id];
        const targetKey =
          entry && typeof entry === 'object' && 'fieldKey' in entry ? entry.fieldKey : null;
        const targetField = targetKey ? targets.fields.find((t) => t.key === targetKey) : null;
        return (
          <div key={f.id} className="space-y-2">
            <h3 className="font-medium text-sm">{f.label}</h3>
            {f.options.length === 0 ? (
              <p className="text-fg-muted text-sm">No options discovered for this field.</p>
            ) : targetField ? (
              <OptionsForField
                field={f}
                targetFieldId={targetField.id}
                mapping={mapping}
                onChange={onChange}
              />
            ) : (
              <p className="text-fg-muted text-sm">
                Options are created automatically when this field is created new — map it to an
                existing field above to choose target options here.
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}

function OptionsForField({
  field,
  targetFieldId,
  mapping,
  onChange,
}: {
  field: DiscoveredField;
  targetFieldId: string;
  mapping: ImportMapping;
  onChange: (next: ImportMapping) => void;
}) {
  const options = useFieldOptions(targetFieldId);
  if (options.isPending) {
    return <Skeleton className="h-24 w-full" />;
  }
  const targetOptions: SelectOption[] = (options.data ?? []).map((o) => ({
    value: o.id,
    label: o.label,
  }));
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Source option</TableHead>
          <TableHead>Target</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {field.options.map((o) => {
          const key = `${field.id}:${o.id}`;
          const entry = mapping.options[key];
          const value = entry && 'optionId' in entry ? entry.optionId : 'create';
          return (
            <TableRow key={o.id}>
              <TableCell className="font-medium">{o.label}</TableCell>
              <TableCell>
                <SimpleSelect
                  aria-label={`Target for option ${o.label}`}
                  value={value}
                  onChange={(v) =>
                    onChange({
                      ...mapping,
                      options: {
                        ...mapping.options,
                        [key]:
                          v && v !== 'create' ? { optionId: v } : { create: { label: o.label } },
                      },
                    })
                  }
                  options={[{ value: 'create', label: 'Create new option' }, ...targetOptions]}
                />
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
